import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { useLanguage } from "./contexts/LanguageContext";
import { useToast } from "./contexts/ToastContext";
import { useDataStore } from "./store/data.store";
import { Header } from "./components/layout/Header";
import { PWAInstallBanner } from "./components/PWAInstallBanner";
import { LoginPage } from "./pages/LoginPage";
import { fetchAllCertifications, fetchUsersDirectory } from "./services/universityApi";
import { fetchPeople, ApiError } from "./services/peopleApi";

const LigaPage = lazy(() => import("./pages/LigaPage").then((m) => ({ default: m.LigaPage })));
const GUsPage = lazy(() => import("./pages/GUsPage").then((m) => ({ default: m.GUsPage })));
const PessoasPage = lazy(() => import("./pages/PessoasPage").then((m) => ({ default: m.PessoasPage })));
const PessoaDetailPage = lazy(() =>
  import("./pages/PessoaDetailPage").then((m) => ({ default: m.PessoaDetailPage })),
);
const ConquistasPage = lazy(() =>
  import("./pages/ConquistasPage").then((m) => ({ default: m.ConquistasPage })),
);
const RegrasPage = lazy(() =>
  import("./pages/RegrasPage").then((m) => ({ default: m.RegrasPage })),
);
const AdminPage = lazy(() => import("./pages/AdminPage").then((m) => ({ default: m.AdminPage })));

const Spinner: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex flex-col items-center justify-center h-full gap-3 min-h-[200px]">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: "var(--accent)" }} />
    {label && <p className="text-xs" style={{ color: "var(--text-3)" }}>{label}</p>}
  </div>
);

export const DataLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loadData, loadDataWithCertifications, enrichNamesFromLms, setApiError, isLoading, apiError } =
    useDataStore();
  const { user, refreshSession, signOut } = useAuth();
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [loadLabel, setLoadLabel] = useState(t.loading.data);
  // Bumped by the retry/reconnect buttons to re-trigger the load effect.
  const [reloadKey, setReloadKey] = useState(0);
  // Shown when the Google access token expired and silent refresh failed.
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoadLabel(t.loading.participants);
        // DataLoader only renders inside AppLayout after the `if (!user)` guard,
        // so user is guaranteed non-null here. Guard defensively just in case.
        const accessToken = user?.accessToken ?? "";
        const { data: colaboradores } = await fetchPeople(accessToken);
        if (cancelled) return;

        try {
          setLoadLabel(t.loading.certifications);
          const certMap = await fetchAllCertifications();
          if (!cancelled) loadDataWithCertifications(colaboradores, certMap);
        } catch (err) {
          // University API failed: log so the failure is debuggable, then fall
          // back to mock localStorage data so the dashboard still renders.
          console.error("[universityApi] integração falhou, usando mock:", err);
          if (!cancelled) loadData(colaboradores);
        }

        // Enriquece o name com first+last do TalentLMS. Não-bloqueante.
        try {
          const directory = await fetchUsersDirectory();
          if (!cancelled) enrichNamesFromLms(directory);
        } catch (err) {
          console.warn("[universityApi] fetchUsersDirectory falhou:", err);
        }
      } catch (err) {
        if (cancelled) return;

        // Token expired → try a silent refresh first. On success the user state
        // rotates (new accessToken) and this effect re-runs automatically, so
        // the user sees nothing. On failure we show an explicit reconnect screen.
        if (err instanceof ApiError && err.isAuth) {
          setLoadLabel(t.loading.reconnecting);
          const refreshed = await refreshSession();
          if (cancelled) return;
          if (refreshed) return; // user change re-triggers the effect
          setSessionExpired(true);
          return;
        }

        // People API itself failed — there is no bundled CSV fallback anymore,
        // so surface a visible error instead of a silent empty dashboard.
        const message = err instanceof Error ? err.message : String(err);
        console.error("[peopleApi] falhou ao carregar colaboradores:", err);
        setApiError(message);
        showToast(t.loadError.message, "error");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [loadData, loadDataWithCertifications, enrichNamesFromLms, setApiError, showToast, refreshSession, user, t, reloadKey]);

  const retry = useCallback(() => {
    setApiError(null);
    useDataStore.setState({ isLoading: true });
    setReloadKey((k) => k + 1);
  }, [setApiError]);

  const reconnect = useCallback(async () => {
    // Triggered by an explicit click (user gesture) → popup is not blocked.
    const refreshed = await refreshSession();
    if (refreshed) {
      setSessionExpired(false);
      useDataStore.setState({ isLoading: true });
      setReloadKey((k) => k + 1);
    } else {
      // Google session itself is dead — fall back to the full login screen.
      signOut();
    }
  }, [refreshSession, signOut]);

  if (sessionExpired) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 min-h-[200px] text-center px-4">
        <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
          {t.sessionExpired.title}
        </p>
        <p className="text-xs" style={{ color: "var(--text-3)" }}>
          {t.sessionExpired.message}
        </p>
        <button
          type="button"
          onClick={reconnect}
          className="mt-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ background: "var(--accent)" }}
        >
          {t.sessionExpired.reconnect}
        </button>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 min-h-[200px] text-center px-4">
        <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
          {t.loadError.title}
        </p>
        <p className="text-xs" style={{ color: "var(--text-3)" }}>
          {t.loadError.message}
        </p>
        <button
          type="button"
          onClick={retry}
          className="mt-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ background: "var(--accent)" }}
        >
          {t.loadError.retry}
        </button>
      </div>
    );
  }

  if (isLoading) return <Spinner label={loadLabel} />;
  return <>{children}</>;
};

const AppLayout: React.FC = () => {
  const { user, isLoading } = useAuth();
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  if (isLoading) return <Spinner />;
  if (!user) return <LoginPage />;

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <Header />
      <PWAInstallBanner />
      <main ref={mainRef} className="flex-1 overflow-auto">
        <DataLoader>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/league" element={<LigaPage />} />
              <Route path="/groups" element={<GUsPage />} />
              <Route path="/people" element={<PessoasPage />} />
              <Route path="/people/:id" element={<PessoaDetailPage />} />
              <Route path="/achievements" element={<ConquistasPage />} />
              <Route path="/rules" element={<RegrasPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<Navigate to="/league" replace />} />
            </Routes>
          </Suspense>
        </DataLoader>
      </main>
    </div>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/*" element={<AppLayout />} />
    </Routes>
  </BrowserRouter>
);

export default App;
